import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { salesApi } from '../../services/api';
import { useAuth } from '../../store/authContext';
import { formatCurrency, formatIndianCompact } from '../../utils/formatters';
import logger from '../../utils/logger';
import './SalesDashboard.css';

const STAGE_LABELS = {
  pending: 'Pending',
  approved: 'Approved',
  eligible_for_invoice: 'Eligible for Invoice',
  invoice_raised: 'Invoice Raised',
  settled: 'Settled',
};

/**
 * Compute Indian Financial Year quarter boundaries.
 * FY quarters: Q1=Apr-Jun, Q2=Jul-Sep, Q3=Oct-Dec, Q4=Jan-Mar
 */
function getQuarterDates(type) {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth(); // 0-indexed

  // Determine current FY quarter
  // Apr(3)-Jun(5)=Q1, Jul(6)-Sep(8)=Q2, Oct(9)-Dec(11)=Q3, Jan(0)-Mar(2)=Q4
  let qStart, qEnd, fyStart;
  if (m >= 3 && m <= 5) {
    // Q1: Apr-Jun
    qStart = new Date(y, 3, 1);
    qEnd = new Date(y, 5, 30);
    fyStart = y;
  } else if (m >= 6 && m <= 8) {
    // Q2: Jul-Sep
    qStart = new Date(y, 6, 1);
    qEnd = new Date(y, 8, 30);
    fyStart = y;
  } else if (m >= 9 && m <= 11) {
    // Q3: Oct-Dec
    qStart = new Date(y, 9, 1);
    qEnd = new Date(y, 11, 31);
    fyStart = y;
  } else {
    // Q4: Jan-Mar (FY started previous year)
    qStart = new Date(y, 0, 1);
    qEnd = new Date(y, 2, 31);
    fyStart = y - 1;
  }

  const fmt = (d) => d.toISOString().slice(0, 10);

  switch (type) {
    case 'current_quarter':
      return { from_date: fmt(qStart), to_date: fmt(qEnd) };
    case 'previous_quarter': {
      // Move back one quarter
      const pqStart = new Date(qStart);
      pqStart.setMonth(pqStart.getMonth() - 3);
      const pqEnd = new Date(qStart);
      pqEnd.setDate(pqEnd.getDate() - 1);
      return { from_date: fmt(pqStart), to_date: fmt(pqEnd) };
    }
    case 'current_year':
      return { from_date: fmt(new Date(fyStart, 3, 1)), to_date: fmt(new Date(fyStart + 1, 2, 31)) };
    case 'previous_year':
      return { from_date: fmt(new Date(fyStart - 1, 3, 1)), to_date: fmt(new Date(fyStart, 2, 31)) };
    case 'all_time':
      return {};
    default:
      return {};
  }
}

const QUICK_FILTERS = [
  { value: 'current_quarter', label: 'Current Quarter' },
  { value: 'current_year', label: 'Current FY' },
  { value: 'previous_quarter', label: 'Previous Quarter' },
  { value: 'previous_year', label: 'Previous FY' },
  { value: 'all_time', label: 'All Time' },
];

export default function SalesDashboard() {
  const { user: currentUser } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [quickFilter, setQuickFilter] = useState('current_quarter');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [agentFilter, setAgentFilter] = useState('');
  const [projectFilter, setProjectFilter] = useState('');
  const [filterOptions, setFilterOptions] = useState({ agents: [], projects: [] });

  useEffect(() => {
    if (currentUser?.role !== 'admin') return;
    salesApi.filterOptions().then((res) => setFilterOptions(res.data)).catch(() => {});
  }, [currentUser?.role]);

  const fetchStats = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      let params;
      if (customFrom || customTo) {
        params = {};
        if (customFrom) params.from_date = customFrom;
        if (customTo) params.to_date = customTo;
      } else {
        params = getQuarterDates(quickFilter);
      }
      if (agentFilter) params.agent_id = agentFilter;
      if (projectFilter) params.project_name = projectFilter;
      const res = await salesApi.dashboard(params);
      setStats(res.data);
    } catch (err) {
      logger.error('api', 'Failed to load sales dashboard', { error: err.message });
      setError('Failed to load sales dashboard.');
    } finally {
      setLoading(false);
    }
  }, [quickFilter, customFrom, customTo, agentFilter, projectFilter]);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  const handleQuickFilter = (value) => {
    setQuickFilter(value);
    setCustomFrom('');
    setCustomTo('');
  };

  const handleCustomDate = (field, value) => {
    setQuickFilter('');
    if (field === 'from') setCustomFrom(value);
    else setCustomTo(value);
  };

  const isAdmin = currentUser?.role === 'admin';

  if (loading && !stats) {
    return (
      <div className="sd-page">
        <div className="sd-loading">
          <div className="sd-loading-spinner" />
          <p>Loading sales dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="sd-page">
      {/* Header */}
      <div className="page-header animate-fade-up">
        <div className="page-header-top">
          <div>
            <h1 className="page-title">Sales Dashboard</h1>
            <p className="page-subtitle">
              {stats ? `${stats.total_count} sale${stats.total_count !== 1 ? 's' : ''}` : ''} in selected period
            </p>
          </div>
          <div className="page-actions">
            <Link to="/sales/all" className="btn btn-ghost btn-sm">View All Sales</Link>
            <button className="btn btn-primary btn-sm" onClick={() => navigate('/sales/new')}>
              + New Sale
            </button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="sd-filters animate-fade-up" style={{ animationDelay: '40ms' }}>
        <div className="sd-quick-filters">
          {QUICK_FILTERS.map((f) => (
            <button
              key={f.value}
              className={`filter-chip ${quickFilter === f.value ? 'filter-chip--active' : ''}`}
              onClick={() => handleQuickFilter(f.value)}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div className="sd-date-range">
          <label className="sd-date-label">From</label>
          <input
            type="date"
            className="input sd-date-input"
            value={customFrom}
            onChange={(e) => handleCustomDate('from', e.target.value)}
          />
          <label className="sd-date-label">To</label>
          <input
            type="date"
            className="input sd-date-input"
            value={customTo}
            onChange={(e) => handleCustomDate('to', e.target.value)}
          />
        </div>
      </div>

      {/* Agent & Project filters (admin only) */}
      {isAdmin && (
        <div className="sd-extra-filters animate-fade-up" style={{ animationDelay: '60ms' }}>
          <div className="sd-filter-group">
            <label className="sd-date-label">Agent</label>
            <select
              className="input sd-filter-select"
              value={agentFilter}
              onChange={(e) => setAgentFilter(e.target.value)}
            >
              <option value="">All Agents</option>
              {filterOptions.agents.map((a) => (
                <option key={a.id} value={a.id}>{a.name}</option>
              ))}
            </select>
          </div>
          <div className="sd-filter-group">
            <label className="sd-date-label">Project</label>
            <select
              className="input sd-filter-select"
              value={projectFilter}
              onChange={(e) => setProjectFilter(e.target.value)}
            >
              <option value="">All Projects</option>
              {filterOptions.projects.map((p) => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
          </div>
        </div>
      )}

      {error && <div className="sd-error animate-fade-up">{error}</div>}

      {stats && (
        <>
          {/* Stat cards */}
          <div className={`sd-stats stagger animate-fade-up ${isAdmin ? 'sd-stats--admin' : ''}`} style={{ animationDelay: '80ms' }}>
            <StatCard
              icon={<AreaIcon />}
              value={formatIndianCompact(stats.area_sold)}
              suffix="sq. ft."
              label="Area Sold"
            />
            <StatCard
              icon={<SalesValueIcon />}
              value={formatIndianCompact(stats.sales_value, '₹')}
              label="Sales Value"
              accent
            />
            <StatCard
              icon={<TotalBrokerageIcon />}
              value={formatIndianCompact(stats.total_brokerage, '₹')}
              label="Total Brokerage"
            />
            <StatCard
              icon={<BrokerageIcon />}
              value={formatIndianCompact(stats.brokerage_earned, '₹')}
              label="Brokerage Earned"
              accent
            />
            <div className="stat-card sd-passback-card">
              <div className="stat-card-top">
                <div className="stat-card-icon"><PassbackIcon /></div>
              </div>
              <div className="stat-card-value">
                {formatIndianCompact((stats.total_customer_passback || 0) + (stats.total_broker_passback || 0), '₹')}
              </div>
              <div className="stat-card-label">Total Passback</div>
              <div className="sd-passback-split">
                <div className="sd-passback-row">
                  <span className="sd-passback-label">Customer</span>
                  <span className="sd-passback-value">{formatIndianCompact(stats.total_customer_passback, '₹')}</span>
                </div>
                <div className="sd-passback-row">
                  <span className="sd-passback-label">Broker</span>
                  <span className="sd-passback-value">{formatIndianCompact(stats.total_broker_passback, '₹')}</span>
                </div>
              </div>
            </div>
            <StatCard
              icon={<EligibleIcon />}
              value={formatIndianCompact(stats.eligible_brokerage, '₹')}
              label="Eligible Brokerage"
              accent
            />
            <StatCard
              icon={<PendingIcon />}
              value={formatIndianCompact(stats.brokerage_pending, '₹')}
              label="Brokerage Pending"
            />
            <StatCard
              icon={<PaidIcon />}
              value={formatIndianCompact(stats.brokerage_paid, '₹')}
              label="Brokerage Paid"
              accent
            />
            {isAdmin && stats.company_share != null && (
              <StatCard
                icon={<CompanyShareIcon />}
                value={formatIndianCompact(stats.company_share, '₹')}
                label="Company Share"
              />
            )}
          </div>

          {/* Agent Breakdown (admin only) — above pipeline */}
          {isAdmin && stats.agent_breakdown && stats.agent_breakdown.length > 0 && (
            <div className="sd-section animate-fade-up" style={{ animationDelay: '120ms' }}>
              <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
                <div style={{ padding: '18px 16px 0' }}>
                  <h3 className="card-title">Agent-wise Breakdown</h3>
                </div>
                <div style={{ overflowX: 'auto' }}>
                  <table className="sd-table sd-breakdown-table">
                    <thead>
                      <tr>
                        <th>Agent</th>
                        <th>Sales</th>
                        <th>Area (sq.ft.)</th>
                        <th>Sales Value</th>
                        <th>Brokerage Earned</th>
                        <th>Eligible</th>
                        <th>Pending</th>
                        <th>Paid</th>
                        <th>Company Share</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stats.agent_breakdown.map((agent) => (
                        <tr key={agent.agent_id}>
                          <td>
                            <span className="sd-agent-name">{agent.agent_name}</span>
                          </td>
                          <td className="sd-table-count">{agent.sale_count}</td>
                          <td className="sd-table-value">{formatIndianCompact(agent.area_sold)}</td>
                          <td className="sd-table-value">{formatCurrency(agent.sales_value)}</td>
                          <td className="sd-table-value">{formatCurrency(agent.brokerage_earned)}</td>
                          <td className="sd-table-value">{formatCurrency(agent.eligible_brokerage)}</td>
                          <td className="sd-table-value">{formatCurrency(agent.brokerage_pending)}</td>
                          <td className="sd-table-value">{formatCurrency(agent.brokerage_paid)}</td>
                          <td className="sd-table-value sd-company-share">{formatCurrency(agent.company_share)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="sd-breakdown-total">
                        <td><strong>Total</strong></td>
                        <td className="sd-table-count">
                          <strong>{stats.agent_breakdown.reduce((s, a) => s + a.sale_count, 0)}</strong>
                        </td>
                        <td className="sd-table-value">
                          <strong>{formatIndianCompact(stats.agent_breakdown.reduce((s, a) => s + a.area_sold, 0))}</strong>
                        </td>
                        <td className="sd-table-value">
                          <strong>{formatCurrency(stats.agent_breakdown.reduce((s, a) => s + a.sales_value, 0))}</strong>
                        </td>
                        <td className="sd-table-value">
                          <strong>{formatCurrency(stats.agent_breakdown.reduce((s, a) => s + a.brokerage_earned, 0))}</strong>
                        </td>
                        <td className="sd-table-value">
                          <strong>{formatCurrency(stats.agent_breakdown.reduce((s, a) => s + a.eligible_brokerage, 0))}</strong>
                        </td>
                        <td className="sd-table-value">
                          <strong>{formatCurrency(stats.agent_breakdown.reduce((s, a) => s + a.brokerage_pending, 0))}</strong>
                        </td>
                        <td className="sd-table-value">
                          <strong>{formatCurrency(stats.agent_breakdown.reduce((s, a) => s + a.brokerage_paid, 0))}</strong>
                        </td>
                        <td className="sd-table-value sd-company-share">
                          <strong>{formatCurrency(stats.agent_breakdown.reduce((s, a) => s + a.company_share, 0))}</strong>
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Pipeline */}
          <div className="sd-section animate-fade-up" style={{ animationDelay: isAdmin ? '160ms' : '120ms' }}>
            <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
              <div style={{ padding: '18px 16px 0' }}>
                <h3 className="card-title">Sales Pipeline</h3>
              </div>
              <div className="sd-pipeline">
                {stats.pipeline.map((s) => (
                  <div key={s.stage} className="sd-pipeline-item">
                    <div className="sd-pipeline-stage">
                      <span className={`badge badge-${s.stage}`}>
                        <span className="badge-dot" />
                        {STAGE_LABELS[s.stage] || s.stage}
                      </span>
                    </div>
                    <div className="sd-pipeline-count">{s.count}</div>
                    <div className="sd-pipeline-value">{formatIndianCompact(s.total_value, '₹')}</div>
                    <div className="sd-pipeline-brok">{formatIndianCompact(s.brokerage_earned, '₹')}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

/* ─── Stat Card ─────────────────────────────────────────────────────────── */

function StatCard({ icon, value, suffix, label, accent }) {
  return (
    <div className={`stat-card ${accent ? 'stat-card--accent' : ''}`}>
      <div className="stat-card-top">
        <div className="stat-card-icon">{icon}</div>
      </div>
      <div className="stat-card-value">
        {value}
        {suffix && <span className="sd-stat-suffix">{suffix}</span>}
      </div>
      <div className="stat-card-label">{label}</div>
    </div>
  );
}

/* ─── Icons ─────────────────────────────────────────────────────────────── */

function AreaIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <rect x="2" y="2" width="12" height="12" rx="1" stroke="currentColor" strokeWidth="1.3" />
      <path d="M2 10l4-4 3 3 5-5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function SalesValueIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path d="M8 1v14M5.5 3.5H9a2 2 0 010 4H6M6 7.5h3.5a2 2 0 010 4H5.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function BrokerageIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <circle cx="8" cy="8" r="6.5" stroke="currentColor" strokeWidth="1.3" />
      <path d="M8 4v8M6 6h4M6 10h4" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
    </svg>
  );
}

function EligibleIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path d="M3 8.5l3.5 3.5L13 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function PendingIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <circle cx="8" cy="8" r="6.5" stroke="currentColor" strokeWidth="1.3" />
      <path d="M8 4.5V8l2.5 2.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function PaidIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <rect x="2" y="4" width="12" height="9" rx="1.5" stroke="currentColor" strokeWidth="1.3" />
      <path d="M2 7h12" stroke="currentColor" strokeWidth="1.3" />
      <path d="M5 10h3" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
    </svg>
  );
}

function TotalBrokerageIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <circle cx="8" cy="8" r="6.5" stroke="currentColor" strokeWidth="1.3" />
      <path d="M8 4v8M5.5 6.5h5M5.5 9.5h5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
    </svg>
  );
}

function PassbackIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path d="M10 3H4.5A1.5 1.5 0 003 4.5v7A1.5 1.5 0 004.5 13H10" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
      <path d="M8 8h5M11 6l2 2-2 2" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function CompanyShareIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <rect x="2" y="2" width="12" height="12" rx="2" stroke="currentColor" strokeWidth="1.3" />
      <path d="M6 6h4M6 8h4M6 10h2" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
    </svg>
  );
}
